import React, {useState, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    Typography,
    Paper,
    Switch,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Template, IWorksheet, IFieldDTO, IAvailableWorksheetColumnsProps, IWorksheetColumn } from '../../types/types';


export interface AvailableWorksheetColumnsRef {
    isDirty: boolean;
    getCurrentTemplate: () => Template | null;
    getCurrentOriginalTemplate: () => Template | null;
    reset: () => boolean;
}
interface AvailableWorksheetColumnsProps {
    templateWorksheetColumnsProps: IAvailableWorksheetColumnsProps;
}

const AvailableWorksheetColumns = forwardRef<AvailableWorksheetColumnsRef, AvailableWorksheetColumnsProps>(
    ({ templateWorksheetColumnsProps }, ref) => {
        const [currentTemplate, setCurrentTemplate] = useState<Template | null>(templateWorksheetColumnsProps.incomingTemplate);
        const currentOriginalTemplate = templateWorksheetColumnsProps.incomingTemplate;
        const lastSavedTemplate = (JSON.parse(JSON.stringify(templateWorksheetColumnsProps.lastSavedTemplate)));
        const currentWorksheetId = templateWorksheetColumnsProps.currentWorksheetId;
        const onDirtyChange: Function = templateWorksheetColumnsProps.onDirtyChange;
        const onLoadingChange = templateWorksheetColumnsProps.onLoadingChange;
        const worksheetFieldGroup = currentTemplate?.worksheets.find((sheet) => sheet.id === currentWorksheetId)?.fieldGroup;

        // Resync local state whenever the parent supplies a new incoming template (e.g. after a
        // successful save, the parent refreshes props with the persisted template so real,
        // non-zero WorksheetColumn Ids are used on any subsequent save/retry).
        useEffect(() => {
            setCurrentTemplate(currentOriginalTemplate);
        }, [currentOriginalTemplate]);

        const [fields, setFields] = useState<IFieldDTO[] | null>(null);
        const [rows, setRows] = useState<any[]>([]);
        const [isLoadingFields, setIsLoadingFields] = useState<boolean>(true);
        // Compute isDirty by comparing current state with original
        const isDirty = React.useMemo(() => {

            if (!currentTemplate || !lastSavedTemplate) return false;

            let current = JSON.stringify(currentTemplate);
            let lastSaved = JSON.stringify(lastSavedTemplate);

            if (current !== lastSaved) {
                return true;
            } else {
                return false;
            }

        }, [currentTemplate, lastSavedTemplate]);

        const isCurrentTemplateDirty = React.useMemo(() => {

            if (!currentTemplate || !currentOriginalTemplate) return false;

            return JSON.stringify(currentTemplate) !== JSON.stringify(currentOriginalTemplate);
        }, [currentTemplate, currentOriginalTemplate]);


        // Notify parent component when dirty state changes
        useEffect(() => {
            onDirtyChange?.(isDirty);
        }, [isDirty, onDirtyChange]);

        // Notify parent component when loading state changes so it can show a full-dialog backdrop
        useEffect(() => {
            onLoadingChange?.(isLoadingFields);
        }, [isLoadingFields, onLoadingChange]);

        useEffect(() => {
            if (worksheetFieldGroup === undefined) {
                return;
            }

            const fetchFields = async () => {
                try {
                    const response = await fetch(`api/template/fields?fieldGroupId=${worksheetFieldGroup}`);

                    if (!response.ok) {
                        throw new Error('Failed to fetch fields');
                    }

                    const data: IFieldDTO[] = await response.json();

                    setFields(data);
                    setRows(cleanAndSortFields(data));

                } catch (error) {
                    console.error('Error fetching fields:', error);
                    alert('Failed to load fields');
                } finally {
                    setIsLoadingFields(false);
                }
            };


            fetchFields();
        }, [worksheetFieldGroup]);

        const compareFieldsToWorksheet = (fields: IFieldDTO[]) => {
            let worksheet = currentTemplate?.worksheets.find((sheet) => sheet.id === currentWorksheetId);

            if (!worksheet) {
                return;
            }
            
            for (let i = 0; i < fields.length; i++) {
                let f = fields[i];

                let fieldInUse = worksheet.worksheetColumns?.find(item => item.mapToFieldId === f.id);

                if (!fieldInUse) {
                    continue;
                }

                if (fieldInUse.customName) {
                    f.customName = fieldInUse.customName;
                }

                f.isEnabled = true;
            }
        }

        const handleToggleWorksheetColumn = useCallback(
            (id: number, checked: boolean, fields: IFieldDTO[] | null) => {
                if (!currentTemplate) return;

                let f = fields?.find(item => item.id === id);

                if (!f) return;

                f.isEnabled = checked;
                
                const copy = JSON.parse(JSON.stringify(currentTemplate));
                const sheet = copy.worksheets.find((sheet: IWorksheet) => sheet.id === currentWorksheetId);

                if (!sheet) return;

                if (checked) {
                    let wc: any = {
                        id: 0,
                        customName: f.customName,
                        name: f.name,
                        isRequired: f.isRequired,
                        fieldIsRequired: f.isRequired,
                        fieldName: f.name,
                        worksheetId: currentWorksheetId,
                        mapToField: f.label,
                        mapToFieldId: id,
                        order: sheet.worksheetColumns!.length + 1, //order it at the end
                        isPrimaryKey: false,
                        isForeignKey: false,
                        insertedByUserId: -1,
                        dataTypeId: f.dataTypeId
                    };

                    //TODO: find out if we'll ever have 0 worksheetColumns?!?!?
                    
                    sheet.worksheetColumns.push(wc);
                    setCurrentTemplate(copy);

                } else {
                    let index = sheet.worksheetColumns?.findIndex((item:any) => item.mapToFieldId === id) || -1;

                    if (index > -1) {
                        sheet.worksheetColumns?.splice(index, 1);
                    }

                    setCurrentTemplate(copy);
                }
            },
            [currentTemplate]
        );

       
        // Reset changes
        const resetChanges = useCallback(() => {

            if (isCurrentTemplateDirty && !window.confirm('Discard changes?')) {
                return false;
            }

            if (currentOriginalTemplate) {
                setCurrentTemplate(JSON.parse(JSON.stringify(currentOriginalTemplate))); // Deep copy
            }

            return true;
        }, [currentOriginalTemplate, isCurrentTemplateDirty]);

        const cleanAndSortFields = (fieldsToClean: IFieldDTO[] | null) => {
            if (!fieldsToClean) {
                return [];
            }

            compareFieldsToWorksheet(fieldsToClean);

            //filter out any fields with null in the name 
            let filteredList = fieldsToClean.filter(item => item.name);

            filteredList.sort((a, b) => {
                if (a.isRequired !== b.isRequired) {
                    return a.isRequired ? -1 : 1;
                }
  
                if (a.name !== b.name) {
                    return a.name < b.name ? -1 : 1
                }

                return 0;
            });

            let rows: IFieldDTO[] = [];

            for (let i = 0; i < filteredList.length; i++) {
                let f = filteredList[i];

                if (f.fieldGroupId === worksheetFieldGroup && f.name) {
                    rows.push(f);
                }
            }

            return rows;
        }

        // DataGrid columns
        const columns: GridColDef[] = [
            {
                field: 'isEnabled',
                headerName: '',
                width: 70,
                sortable: false,
                renderCell: (params: GridRenderCellParams<IWorksheet>) => (
                    <Switch
                        checked={params.row.isEnabled || false}
                        disabled={params.row.isEnabled && params.row.isRequired}
                        onChange={(e: any) => {
                            handleToggleWorksheetColumn(params.row.id, e.target.checked, fields);
                        }}
                        color="primary"
                    />
                ),
            },
            {
                field: 'name',
                headerName: 'Field Name',
                flex: 1,
                minWidth: 200,
                sortable: false,
                renderCell: (params: GridRenderCellParams<IWorksheet>) => (
                    params.row.customName ?? params.row.name
                ),
            },
            {
                field: 'label',
                headerName: 'Database Mapping',
                flex: 1,
                minWidth: 150,
                sortable: false,
                renderCell: (params: GridRenderCellParams<IWorksheet>) => {

                    //check for labels in the form 'table.column', and remove 'table.'
                    let index = params.row.label.indexOf('.');

                    if (index > -1) {
                        return params.row.label.substring(index + 1);
                    }

                    return params.row.label;
                },
            }
        ];

        // Expose methods via ref
        useImperativeHandle(ref, () => ({
            isDirty,
            getCurrentTemplate: () => currentTemplate,
            getCurrentOriginalTemplate: () => currentOriginalTemplate,
            reset: resetChanges,
        }), [isDirty, currentTemplate, currentOriginalTemplate, resetChanges]
        );

        if (isLoadingFields) {
            // Parent Dialog shows a full-dialog Backdrop while loading (via onLoadingChange);
            // render nothing here to avoid a nested/duplicate loading indicator.
            return null;
        }

        if (!rows || rows.length === 0) {
            return (
                <Box sx={{ minHeight: 400, p: 3 }}>
                    <Typography>No columns found</Typography>
                </Box>
            );
        }

        return (
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5" gutterBottom>
                        Available Columns
                    </Typography>
                </div>
                <Box sx={{ width: '100%' }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        disableSelectionOnClick
                        disableColumnMenu
                        density="standard"
                        hideFooter
                        autoHeight
                        sx={{
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: '#E4EDF0',
                            }
                        }}
                    />
                </Box>
            </Paper>
        );

    });


export default AvailableWorksheetColumns;